import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import AuthImage, {openImageInNewWindow} from "../components/AuthImage";
import OutfitScoreBadge, {OutfitScoreDiagnostic} from "../components/OutfitScoreBadge";
import OutfitScoreDetails from "../components/OutfitScoreDetails";
import WeatherLocationForm, {FIXABLE_WEATHER_REASONS} from "../components/WeatherLocationForm";
import {slotLabel, slotRank} from "../support/outfitLabels";
import OutfitReadinessPanel from "../components/outfit/OutfitReadinessPanel";
import OutfitContextPanel from "../components/outfit/OutfitContextPanel";
import OutfitScenarioTiles from "../components/outfit/OutfitScenarioTiles";
import {
    EMPTY_CONTEXT, OutfitContextParams, OutfitScenarioTile, contextParamsSummary,
    contextRequestBody, paramsOfTile,
} from "../support/outfitContext";
import {
    AppliedConstraints, Readiness, constraintsSummary, fallbackReadiness, isReady, splitReasons,
} from "../support/outfitReadiness";
import IconButton from "../components/IconButton";
import {
    AlertTriangleIcon, BookmarkIcon, CalendarIcon, CameraIcon, CheckCheckIcon, CloudIcon,
    HangerIcon, MaximizeIcon, PlusIcon, RefreshIcon, SparklesIcon, SunIcon,
} from "../components/icons";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

// ── types ──────────────────────────────────────────────────────────────────────

interface OutfitItem {
    id: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    slot: string | null;
    categoryId?: string | null;
    /** Вещь закреплена в образе (якорь автосборки, CTHS-313) — её не предлагают заменить. */
    pinned?: boolean;
}

interface Weather {
    available: boolean;
    reason?: string | null;
    city: string | null;
    tempMinC: number | null;
    tempMaxC: number | null;
}

type FitState = 'OK' | 'FAIL' | 'UNKNOWN';

interface Fit {
    aspect: string;
    /** Обратная совместимость: true/false/null; источник истины — state. */
    ok: boolean | null;
    state?: FitState;
    note: string;
}

interface Tip {
    title: string;
    note: string;
}

interface Replacement {
    itemId: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    slot?: string | null;
    score: number;
    delta: number | null;
    /** Улучшает ли замена образ (delta > 0) — бэкенд считает по OutfitScore (F10). */
    improvement?: boolean;
    /** Пригодность вещи: OK | CAUTION (дефекты, ограниченная доступность). */
    suitability?: string | null;
    suitabilityNote?: string | null;
    /** Вид улучшения: ADD_ROLE | REPLACE_IN_ROLE (R03). */
    kind?: string | null;
    /** Почему кандидат не улучшение, хотя балл растёт (ломает полноту или тепло). */
    rejectionReason?: string | null;
    roleLabel?: string | null;
}

/** «Добавить недостающее»: незакрытая роль и вещи, которые её закрывают (R03). */
interface Addition {
    role: string;
    roleLabel: string;
    reason: string;
    candidates: Replacement[];
}

interface SlotReplacements {
    slot: string;
    slotLabel: string;
    current: OutfitItem;
    candidates: Replacement[];
}

interface DailyPick {
    weather: Weather | null;
    /** Повлияла ли погода на подбор — без прогноза соответствие погоде не проверяется (QA-2). */
    weatherApplied?: boolean;
    occasion: string | null;
    outfit: {
        items: OutfitItem[]; complete: boolean; missingSlots: string[];
        reasons?: string[];
        // outfit-score-engine: единая детерминированная оценка варианта
        outfitScore?: number | null; scoreLabel?: string | null;
        topDiagnostics?: OutfitScoreDiagnostic[];
    } | null;
    suggestedName: string | null;
    aiScore: number | null;
    verdict: string | null;
    confidence: string | null;
    confidenceReason: string | null;
    wardrobeNotes: string[] | null;
    /** Причины оценки человеческим языком (F09); техника — в topDiagnostics. */
    scoreReasons?: string[] | null;
    /** Конфликты образа человеческим языком (F09). */
    scoreCautions?: string[] | null;
    fits: Fit[] | null;
    warnings: string[] | null;
    tips: Tip[] | null;
    replacements: SlotReplacements[] | null;
    /** Готовность комплекта — первый блок результата (R01/R11). */
    readiness?: Readiness | null;
    /** Принятые ограничения запроса: ручная температура, осадки, распознанное из текста (R02). */
    appliedConstraints?: AppliedConstraints | null;
    /** Версия состава: всё производное относится именно к ней (R04). */
    compositionVersion?: string | null;
    /** Улучшения вида «добавить недостающую роль» (R03). */
    additions?: Addition[] | null;
    /** Закреплённая вещь автосборки с карточки вещи (CTHS-313); null — обычный образ дня. */
    seedItemId?: string | null;
    seedItemName?: string | null;
    /** Полнота выбранного образа (дубль outfit.complete на верхнем уровне, QA-1). */
    complete?: boolean;
    missingSlots?: string[] | null;
    needsWardrobe: boolean;
    message: string | null;
}

/**
 * Ответ POST /outfits/daily/recompose — детерминированный пересчёт после правки состава (R04).
 * Текстов модели здесь намеренно нет: они относятся к прежнему составу.
 */
interface Composition {
    compositionVersion: string | null;
    items: OutfitItem[];
    outfitScore: number | null;
    scoreLabel: string | null;
    verdict: string | null;
    readiness: Readiness | null;
    appliedConstraints: AppliedConstraints | null;
    scoreReasons: string[];
    scoreCautions: string[];
    additions: Addition[];
    replacements: SlotReplacements[];
}

const OCCASIONS: [string, string][] = [
    ['WORK', 'Офис'], ['CORPORATE', 'Деловая встреча'], ['DATE', 'Свидание'],
    ['WALK', 'Прогулка'], ['CAFE', 'Встреча с друзьями'], ['TRAVEL', 'Поездка'],
    ['SPORT', 'Спорт'], ['EVERYDAY', 'Повседневность'],
];
/**
 * Настроение образа: код → русская подпись (QA-15 — в чипах были «Casual» и «Smart casual»
 * рядом с русскими вариантами). Подписи совпадают со справочниками стиля и формальности
 * («Кэжуал», «Смарт-кэжуал»); в подсказку модели уходит подпись, не код.
 */
const STYLE_VIBE_LABELS: Record<string, string> = {
    CASUAL: 'Кэжуал',
    SMART_CASUAL: 'Смарт-кэжуал',
    BUSINESS: 'Деловой',
    SPORTS: 'Спортивный',
    MINIMALIST: 'Минимализм',
    STRICT: 'Строгий',
};
const STYLE_VIBES = Object.keys(STYLE_VIBE_LABELS);
const COLOR_PREFS = ['Бежевый', 'Синий', 'Чёрный', 'Белый', 'Серый', 'Зелёный'];
const FIT_LABELS: Record<string, Record<FitState, string>> = {
    weather: {OK: 'Подходит под погоду', FAIL: 'Не подходит под погоду', UNKNOWN: 'Погода не учтена'},
    occasion: {OK: 'Подходит под повод', FAIL: 'Не подходит под повод', UNKNOWN: 'Повод не проверен'},
    style: {OK: 'Соответствует стилю', FAIL: 'Не соответствует стилю', UNKNOWN: 'Стиль не проверен'},
    colors: {OK: 'Цвета сочетаются', FAIL: 'Цвета не сочетаются', UNKNOWN: 'Цвета не проверены'},
};
const CONFIDENCE_LABELS: Record<string, string> = {high: 'высокая', medium: 'средняя', low: 'низкая'};

/**
 * Текст ошибки подбора по коду ответа. Коды автосборки с якорем (CTHS-313): 404 — вещь не из
 * этого гардероба, 409 — вещь в корзине или выбыла, 403 — нет права смотреть вещи гардероба.
 */
export function pickErrorMessage(code?: number): string {
    if (code === 402) return 'Нужен AI-ключ для подбора (настройте в настройках)';
    if (code === 404) return 'Вещь не найдена в этом гардеробе — образ вокруг неё собрать нельзя';
    if (code === 409) return 'Вещь в корзине или выбыла из гардероба — образ вокруг неё собрать нельзя';
    if (code === 403) return 'Нет доступа к вещам этого гардероба';
    return 'Не удалось собрать образ';
}

/** Третье состояние соответствия: state от бэкенда, иначе — по трёхзначному ok. */
export function fitState(fit: Fit): FitState {
    if (fit.state === 'OK' || fit.state === 'FAIL' || fit.state === 'UNKNOWN') return fit.state;
    if (fit.ok === true) return 'OK';
    if (fit.ok === false) return 'FAIL';
    return 'UNKNOWN';
}

function fitLabel(fit: Fit, state: FitState): string {
    return FIT_LABELS[fit.aspect]?.[state] ?? fit.aspect;
}

function scoreTone(v: number): {ring: string; text: string} {
    if (v >= 70) return {ring: 'stroke-green-500', text: 'text-green-500'};
    if (v >= 40) return {ring: 'stroke-amber-400', text: 'text-amber-400'};
    return {ring: 'stroke-red-500', text: 'text-red-500'};
}

const ScoreRing: React.FC<{value: number}> = ({value}) => {
    const r = 40, c = 2 * Math.PI * r, off = c * (1 - Math.max(0, Math.min(100, value)) / 100);
    const tone = scoreTone(value);
    return (
        <svg viewBox="0 0 100 100" className="w-24 h-24">
            <circle cx="50" cy="50" r={r} className="stroke-gray-200 dark:stroke-ink-700" strokeWidth="9" fill="none"/>
            <circle cx="50" cy="50" r={r} className={tone.ring} strokeWidth="9" fill="none" strokeLinecap="round"
                    strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 50 50)"/>
            <text x="50" y="56" textAnchor="middle" className="fill-gray-900 dark:fill-gray-100 font-bold" fontSize="26">{value}</text>
        </svg>
    );
};

const Chip: React.FC<{active: boolean; onClick: () => void; children: React.ReactNode}> = ({active, onClick, children}) => (
    <button onClick={onClick}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${active
                ? 'bg-brand-50 dark:bg-brand-900/30 border-brand-400 text-brand-700 dark:text-brand-300 font-medium'
                : 'border-gray-200 dark:border-ink-700 text-gray-600 dark:text-gray-300 hover:border-brand-300'}`}>
        {children}
    </button>
);

// ── component ──────────────────────────────────────────────────────────────────

const OutfitPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {selectedOwnerId} = useWardrobe();
    const ownerParam = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
    // CTHS-173/175: CTA «Подобрать образ» в блоке «Сегодня и скоро» приносит событие адресом.
    // Чужой или устаревший идентификатор бэкенд молча игнорирует — подбор не падает.
    const eventId = searchParams.get('eventId');
    /**
     * CTHS-313: якорь автосборки приходит из адреса (`/outfit?seedItemId=…`) — переход с
     * карточки вещи. В этом режиме мастер контекста пропускается: пользователь уже сказал,
     * вокруг чего собирать образ, и ждёт результат, а не три шага настройки.
     */
    const seedItemId = searchParams.get('seedItemId');

    const [step, setStep] = useState<1 | 2 | 3>(seedItemId ? 3 : 1);
    const [occasion, setOccasion] = useState('WORK');
    const [notes, setNotes] = useState('');
    // R02: ручные температура и осадки — ограничение подбора, работающее без геолокации.
    const [manualTemp, setManualTemp] = useState('');
    const [manualRain, setManualRain] = useState<boolean | null>(null);
    const [vibes, setVibes] = useState<string[]>([]);
    const [colors, setColors] = useState<string[]>([]);
    // CTHS-153/154/161: явные измерения контекста — необязательные, свёрнуты по умолчанию.
    const [context, setContext] = useState<OutfitContextParams>(EMPTY_CONTEXT);
    const [scenarios, setScenarios] = useState<OutfitScenarioTile[]>([]);
    const [scenariosLoading, setScenariosLoading] = useState(true);
    const [scenariosError, setScenariosError] = useState<string | null>(null);
    const [scenarioBusy, setScenarioBusy] = useState<string | null>(null);

    const [result, setResult] = useState<DailyPick | null>(null);
    const [picking, setPicking] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [exclude, setExclude] = useState<string[]>([]);
    const [evalMsg, setEvalMsg] = useState<string | null>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    // F10: альтернатива применяется в текущий комплект («Заменить»), а не открывается в новом
    // окне. composition === null — исходный состав от подбора; иначе — состав после замен.
    const [composition, setComposition] = useState<OutfitItem[] | null>(null);
    // R04: производные данные нового состава приходят из пересборки, а не домысливаются клиентом.
    const [recomposed, setRecomposed] = useState<Composition | null>(null);
    // R08: стек правок — «Вернуть прежнюю» отменяет одну замену, не сбрасывая остальные.
    const [undoStack, setUndoStack] = useState<OutfitItem[][]>([]);
    const [scoring, setScoring] = useState(false);
    const [replaceMsg, setReplaceMsg] = useState<string | null>(null);

    useDocumentTitle();

    const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
        set(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

    /** Ручная температура как число; пустая строка и мусор — «не указано». */
    const manualTempValue = (): number | null => {
        const raw = manualTemp.trim().replace(',', '.').replace('\u2212', '-');
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed >= -60 && parsed <= 60 ? parsed : null;
    };

    const buildNotes = (): string => {
        const parts = [notes.trim()];
        if (vibes.length) parts.push(`Стиль: ${vibes.map(v => STYLE_VIBE_LABELS[v] ?? v).join(', ')}.`);
        if (colors.length) parts.push(`Предпочтительные цвета: ${colors.join(', ')}.`);
        return parts.filter(Boolean).join(' ');
    };

    const pick = useCallback(async (excludeIds: string[]) => {
        setPicking(true);
        setMsg(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<DailyPick>('/outfits/daily/pick',
                {
                    occasion, notes: buildNotes(), exclude: excludeIds,
                    manualTempC: manualTempValue(), manualPrecipitation: manualRain,
                    eventId: eventId ?? undefined,
                    ...contextRequestBody(context),
                    ...(seedItemId ? {seedItemId} : {}),
                }, {params: ownerParam});
            setResult(res.data);
            // Новый подбор — новый исходный состав: прежние замены и их оценка неактуальны.
            setComposition(null);
            setRecomposed(null);
            setUndoStack([]);
            setReplaceMsg(null);
            setStep(3);
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const code = (e as any)?.response?.status;
            setMsg(pickErrorMessage(code));
        } finally {
            setPicking(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [occasion, notes, vibes, colors, manualTemp, manualRain, context, selectedOwnerId, eventId, seedItemId]);

    // Автосборка стартует сама: пользователь нажал «Подобрать образ» на карточке вещи, и
    // повторное нажатие на этой странице было бы лишним шагом. Ref — чтобы StrictMode и
    // повторные рендеры не слали второй запрос.
    const seedStarted = useRef(false);
    useEffect(() => {
        if (!seedItemId || seedStarted.current) return;
        seedStarted.current = true;
        setStep(3);
        pick([]);
    }, [seedItemId, pick]);

    // Сценарные тайлы грузятся один раз: справочник статический и от гардероба не зависит.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<OutfitScenarioTile[]>('/outfits/scenarios');
                if (alive) setScenarios(Array.isArray(res.data) ? res.data : []);
            } catch {
                // Тайлы — ускорение, а не единственный путь: форма ниже работает и без них.
                if (alive) setScenariosError('Сценарии сейчас недоступны — соберите образ формой ниже');
            } finally {
                if (alive) setScenariosLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    /**
     * Быстрое действие: образ по сценарному тайлу одним запросом (CTHS-159). Выбранные тайлом
     * параметры остаются в панели — пользователю видно, что именно применилось, и он может
     * уточнить их, не начиная заново.
     */
    const quickPick = async (tile: OutfitScenarioTile) => {
        setScenarioBusy(tile.key);
        setPicking(true);
        setMsg(null);
        setOccasion(tile.occasion);
        setContext(paramsOfTile(tile));
        try {
            const api = await createAxiosInstance();
            const res = await api.post<DailyPick>('/outfits/quick-action', {
                scenario: tile.key,
                notes: buildNotes(),
                manualTempC: manualTempValue(),
                manualPrecipitation: manualRain,
            }, {params: ownerParam});
            setResult(res.data);
            setComposition(null);
            setRecomposed(null);
            setUndoStack([]);
            setReplaceMsg(null);
            setExclude([]);
            setStep(3);
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setMsg(pickErrorMessage((e as any)?.response?.status));
        } finally {
            setScenarioBusy(null);
            setPicking(false);
        }
    };

    const anotherPick = () => {
        const shown = (composition ?? result?.outfit?.items ?? []).map(i => i.id);
        // Якорь не исключается никогда: «другой вариант» в режиме автосборки — это другой образ
        // вокруг той же вещи, а не образ без неё.
        const next = [...exclude, ...shown].filter(id => id !== seedItemId);
        setExclude(next);
        pick(next);
    };

    // Погода — из ответа подбора (один источник с генерацией и оценкой, QA-2), а не отдельный запрос.
    const weather = result?.weather ?? null;

    // «Надеть образ»: сохраняем образ (с предложенным ИИ названием), отмечаем носку и
    // переходим на страницу образа.
    const [wearing, setWearing] = useState(false);
    const [savingOnly, setSavingOnly] = useState(false);

    /** Сохраняет текущий состав как SavedOutfit и возвращает id (или null при ошибке). */
    const saveComposition = async (): Promise<string | null> => {
        const its = composition ?? result?.outfit?.items ?? [];
        if (its.length === 0) return null;
        try {
            const api = await createAxiosInstance();
            const items = its.map((it, idx) => ({itemId: it.id, slot: it.slot ?? undefined, sortOrder: idx}));
            const created = await api.post<{id: string}>('/outfits/saved',
                {name: result?.suggestedName || 'Образ дня', items}, {params: ownerParam});
            return created.data.id;
        } catch {
            setMsg('Не удалось сохранить образ');
            return null;
        }
    };

    /** «Сохранить образ» — без отметки носки: образ уходит в «Мои образы» (CTHS-291). */
    const saveThis = async () => {
        setSavingOnly(true);
        const id = await saveComposition();
        if (id) {
            navigate(`/outfits/saved/${id}`);
            return;
        }
        setSavingOnly(false);
    };

    const wearThis = async () => {
        setWearing(true);
        const id = await saveComposition();
        if (!id) {
            setWearing(false);
            return;
        }
        try {
            // Контекст носки — для истории образа.
            const api = await createAxiosInstance();
            const ctx = {
                occasion,
                weatherTempMinC: weather?.tempMinC != null ? Math.round(weather.tempMinC) : undefined,
                weatherTempMaxC: weather?.tempMaxC != null ? Math.round(weather.tempMaxC) : undefined,
            };
            await api.post(`/outfits/saved/${id}/wear`, ctx, {params: ownerParam});
        } catch { /* носка не критична для перехода */ }
        navigate(`/outfits/saved/${id}`);
    };

    // «Дополнить»: неполный образ уходит в конструктор с предзаполненными слотами (QA-1).
    const completeInBuilder = () => {
        const its = composition ?? result?.outfit?.items ?? [];
        navigate('/outfits/builder', {
            state: {
                prefillItems: its.map(it => ({
                    id: it.id, name: it.name, brandName: it.brandName, primaryPhotoUrl: it.primaryPhotoUrl,
                    slot: it.slot, categoryId: it.categoryId ?? null,
                })),
            },
        });
    };

    /**
     * R04: любая правка состава пересобирает всё производное. Раньше клиент пересчитывал только
     * балл через POST /outfits/score, и рядом с новым составом оставались название «Кэжуал-прогулка
     * с овершётом» и советы по овершёрту, которого в образе уже нет.
     */
    const recompose = useCallback(async (next: OutfitItem[]) => {
        setReplaceMsg(null);
        setScoring(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<Composition>('/outfits/daily/recompose', {
                itemIds: next.map(it => it.id),
                occasion,
                notes: buildNotes(),
                manualTempC: manualTempValue(),
                manualPrecipitation: manualRain,
                // R04 + CTHS-153: пересборка обязана видеть тот же контекст, иначе после замены
                // «велосипед» и «весь день» перестали бы действовать на полноту комплекта.
                ...contextRequestBody(context),
            }, {params: ownerParam});
            setRecomposed(res.data);
        } catch {
            setRecomposed(null);
            setReplaceMsg('Состав изменён, но пересчитать образ не удалось — обновите подбор');
        } finally {
            setScoring(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [occasion, notes, vibes, colors, manualTemp, manualRain, context, selectedOwnerId]);

    /** Применяет альтернативу в текущий комплект: замена внутри слота либо добавление роли. */
    const applyReplacement = (slotItemId: string | null, slot: string | null, candidate: Replacement) => {
        const base = composition ?? result?.outfit?.items ?? [];
        const replacement: OutfitItem = {
            id: candidate.itemId,
            name: candidate.name,
            brandName: candidate.brandName,
            primaryPhotoUrl: candidate.primaryPhotoUrl,
            slot: slot ?? candidate.slot ?? null,
            categoryId: null,
        };
        const next: OutfitItem[] = slotItemId
            ? base.map(it => (it.id === slotItemId ? replacement : it))
            : [...base, replacement];
        setUndoStack(stack => [...stack, base]);
        setComposition(next);
        void recompose(next);
    };

    /** R08: локальная отмена одной правки — остальные изменения состава сохраняются. */
    const undoLastChange = () => {
        const previous = undoStack[undoStack.length - 1];
        if (!previous) return;
        setUndoStack(stack => stack.slice(0, -1));
        const original = result?.outfit?.items ?? [];
        const backToOriginal = previous.length === original.length
            && previous.every((it, i) => it.id === original[i]?.id);
        if (backToOriginal) {
            setComposition(null);
            setRecomposed(null);
            setReplaceMsg(null);
            return;
        }
        setComposition(previous);
        void recompose(previous);
    };

    /** Возврат к составу, который подобрал AI. */
    const resetComposition = () => {
        setComposition(null);
        setRecomposed(null);
        setUndoStack([]);
        setReplaceMsg(null);
    };

    const uploadPhoto = async (file: File) => {
        setEvalMsg('Анализируем фото…');
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const api = await createAxiosInstance();
            const temp = weather?.tempMaxC != null ? Math.round(weather.tempMaxC) : undefined;
            const res = await api.post<{overallScore: number; verdict: string; summary: string}>(
                '/outfit-evaluation', {photoBase64: base64, scenario: occasion, weatherTemp: temp}, {params: ownerParam});
            setEvalMsg(`Оценка фото: ${res.data.overallScore}/10. ${res.data.summary ?? ''}`);
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const code = (e as any)?.response?.status;
            setEvalMsg(code === 402 ? 'Нужен AI-ключ' : code === 403 ? 'Нужно согласие на профилирование по фото' : 'Не удалось проанализировать фото');
        }
    };

    const today = new Date().toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', weekday: 'long'});
    // R02: резюме показывает только то, что продукт действительно учтёт.
    const plannedConstraints = [
        ...constraintsSummary({
            occasionLabel: OCCASIONS.find(([code]) => code === occasion)?.[1],
            tempC: manualTempValue(),
            precipitation: manualRain,
        }),
        ...contextParamsSummary(context),
    ];

    const constraintsSummaryBlock = plannedConstraints.length > 0 ? (
        <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-3 space-y-1"
             data-testid="constraints-summary">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Будет учтено</p>
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
                {plannedConstraints.map((text, i) => (
                    <li key={i} className="text-xs text-gray-700 dark:text-gray-300">{text}</li>
                ))}
            </ul>
        </div>
    ) : null;

    // ── step 1 + 2: wizard ──
    if (step !== 3) {
        return (
            <div className="p-4 md:p-6 space-y-6 max-w-3xl">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Подбери образ на сегодня</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        AI подберёт лучший образ из твоего гардероба с учётом контекста
                    </p>
                </div>

                {/* Steps */}
                <div className="flex items-center gap-2 text-sm">
                    {[[1, 'Контекст'], [2, 'Стиль'], [3, 'Результат']].map(([n, label]) => (
                        <span key={n as number}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${step === n
                                  ? 'bg-brand-500 text-white' : 'text-gray-400'}`}>
                            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{n as number}</span>
                            {label}
                        </span>
                    ))}
                </div>

                {msg && (
                    <div className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-ink-800 text-sm text-gray-700 dark:text-gray-300">{msg}</div>
                )}

                {step === 1 && (
                    <div className="space-y-5" data-testid="context-step">
                        <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                            <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-300">
                                <span className="flex items-center gap-1.5"><CalendarIcon className="w-4 h-4"/> {today}</span>
                                <span className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                                    <SunIcon className="w-4 h-4"/> Прогноз подтянем при подборе
                                </span>
                            </div>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                Сценарий
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                Один тап — и образ собран под типовой сценарий. Остальное можно уточнить ниже.
                            </p>
                            <OutfitScenarioTiles tiles={scenarios} loading={scenariosLoading}
                                                 error={scenariosError} busyKey={scenarioBusy}
                                                 onPick={quickPick}/>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Повод</h3>
                            <div className="flex flex-wrap gap-2">
                                {OCCASIONS.map(([code, label]) => (
                                    <Chip key={code} active={occasion === code} onClick={() => setOccasion(code)}>{label}</Chip>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Погода вручную</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                Если знаете температуру — укажите её. Ограничение сработает и без геолокации.
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                    Температура, °C
                                    <input type="number" min={-60} max={60} step={1} value={manualTemp}
                                           onChange={e => setManualTemp(e.target.value)}
                                           data-testid="manual-temp-input"
                                           aria-label="Температура в градусах Цельсия"
                                           className="w-24 min-h-[44px] px-3 py-2 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100 text-sm"/>
                                </label>
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 min-h-[44px]">
                                    <input type="checkbox" checked={manualRain === true}
                                           onChange={e => setManualRain(e.target.checked ? true : null)}
                                           data-testid="manual-rain-input"
                                           className="w-5 h-5 rounded border-gray-300 dark:border-ink-600"/>
                                    Осадки
                                </label>
                            </div>
                        </div>
                        <OutfitContextPanel value={context} onChange={setContext}/>
                        {constraintsSummaryBlock}
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Дополнительно</h3>
                            <textarea value={notes} onChange={e => setNotes(e.target.value.slice(0, 200))}
                                      placeholder="Например: важная встреча, хочу выглядеть строго, но не скучно"
                                      rows={2}
                                      className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100 text-sm"/>
                        </div>
                        <button onClick={() => setStep(2)} data-testid="continue-btn"
                                className="px-5 py-2.5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 text-sm font-medium">
                            Продолжить
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-5" data-testid="style-step">
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Стиль</h3>
                            <div className="flex flex-wrap gap-2">
                                {STYLE_VIBES.map(v => (
                                    <Chip key={v} active={vibes.includes(v)}
                                          onClick={() => toggle(vibes, setVibes, v)}>
                                        {STYLE_VIBE_LABELS[v]}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Предпочтительные цвета</h3>
                            <div className="flex flex-wrap gap-2">
                                {COLOR_PREFS.map(v => (
                                    <Chip key={v} active={colors.includes(v)} onClick={() => toggle(colors, setColors, v)}>{v}</Chip>
                                ))}
                            </div>
                        </div>
                        {constraintsSummaryBlock}
                        <div className="flex gap-2">
                            <button onClick={() => setStep(1)}
                                    className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-ink-700 text-sm hover:bg-gray-50 dark:hover:bg-ink-800">
                                Назад
                            </button>
                            <button onClick={() => pick(exclude)} disabled={picking} data-testid="pick-btn"
                                    className="px-5 py-2.5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60 text-sm font-medium">
                                {picking ? 'Подбираем…' : 'Подобрать образ'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── автосборка: результата ещё нет ──
    //
    // В режиме якоря мастер пропущен, поэтому состояние «собираем» и ошибка живут здесь,
    // а не на шаге 2 (кнопка подбора там осталась для обычного образа дня).
    if (!result) {
        return (
            <div className="p-4 md:p-6 space-y-4 max-w-2xl" data-testid="seed-pick-pending">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Собираем образ</h1>
                {picking ? (
                    <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-6 flex items-center gap-3"
                         data-testid="seed-pick-loading">
                        <SparklesIcon className="w-5 h-5 text-brand-500 animate-pulse shrink-0"/>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Подбираем образ вокруг выбранной вещи — это занимает несколько секунд.
                        </p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-6 space-y-3"
                         data-testid="seed-pick-error">
                        <p className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <AlertTriangleIcon className="w-4 h-4 text-amber-500 shrink-0 mt-0.5"/>
                            {msg ?? 'Не удалось собрать образ'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => pick(exclude)} data-testid="seed-retry-btn"
                                    className="min-h-[44px] px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700 text-sm font-medium">
                                Попробовать снова
                            </button>
                            <button onClick={() => setStep(1)} data-testid="seed-open-wizard-btn"
                                    className="min-h-[44px] px-4 rounded-xl border border-gray-200 dark:border-ink-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-ink-800">
                                Настроить подбор
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── step 3: result ──
    const r = result;
    const currentItems = composition ?? r.outfit?.items ?? [];
    const items = [...currentItems].sort((a, b) => slotRank(a.slot) - slotRank(b.slot));
    // Оценка исходного подбора и оценка текущего (после замен) состава — для показа Δ.
    const baseScore = r.outfit?.outfitScore ?? r.aiScore ?? null;
    const shownScore = composition ? (recomposed?.outfitScore ?? null) : baseScore;
    const shownScoreLabel = composition ? (recomposed?.scoreLabel ?? null) : (r.outfit?.scoreLabel ?? null);
    const scoreDelta = composition && shownScore != null && baseScore != null
        ? Math.round(shownScore - baseScore) : null;

    // R04: всё производное берётся из одной версии состава — либо из подбора, либо из пересборки.
    const readiness: Readiness | null = composition
        ? (recomposed?.readiness ?? null)
        : (r.readiness ?? fallbackReadiness(r.complete ?? r.outfit?.complete,
            r.missingSlots ?? r.outfit?.missingSlots));
    const appliedConstraints = composition
        ? (recomposed?.appliedConstraints ?? null) : (r.appliedConstraints ?? null);
    const activeReplacements = composition ? (recomposed?.replacements ?? []) : (r.replacements ?? []);
    const activeAdditions: Addition[] = composition ? (recomposed?.additions ?? []) : (r.additions ?? []);
    // Тексты модели относятся к прежнему составу — после правки их не показываем (R04).
    const modelTextsStale = !!composition;

    // F10: кандидаты делятся по объяснимому эффекту замены. R03: замена, ломающая полноту или
    // тепло, улучшением не считается, даже если балл растёт, — бэкенд помечает её причиной отказа.
    const splitGroups = (improving: boolean): SlotReplacements[] => activeReplacements
        .map(sr => ({
            ...sr,
            candidates: sr.candidates.filter(c => (c.improvement ?? (c.delta ?? 0) > 0) === improving),
        }))
        .filter(sr => sr.candidates.length > 0);
    const improvementGroups = splitGroups(true);
    const alternativeGroups = splitGroups(false);

    // R08: слот остаётся доступным после замены — альтернативы пересчитаны для нового состава,
    // и следующую можно примерить сразу, без общего «Вернуть исходный состав».
    const renderReplacementGroup = (sr: SlotReplacements) => {
        return (
            <div key={sr.slot} className="space-y-1.5" data-testid="replacement-group">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                    {sr.slotLabel}: сейчас «{sr.current.name}»
                </p>
                <div className="flex gap-2 flex-wrap">
                    {sr.candidates.map(c => (
                        <div key={c.itemId}
                             data-testid="replacement-candidate"
                             className="flex items-center gap-2.5 rounded-xl border border-gray-200 dark:border-ink-700 pr-2 hover:border-brand-300 transition-colors">
                            <button
                                onClick={() => c.primaryPhotoUrl && openImageInNewWindow(c.primaryPhotoUrl)}
                                title="Открыть фото на весь экран"
                                aria-label={`Открыть фото «${c.name}» на весь экран`}
                                className="relative w-12 h-12 rounded-l-xl overflow-hidden bg-gray-100 dark:bg-ink-800 shrink-0 group/thumb">
                                {c.primaryPhotoUrl
                                    ? <AuthImage imageKey={c.primaryPhotoUrl} alt={c.name} variant="thumbnail" className="w-full h-full object-cover cursor-zoom-in"/>
                                    : <span className="w-full h-full flex items-center justify-center"><SparklesIcon className="w-5 h-5 text-gray-300"/></span>}
                                {c.primaryPhotoUrl && (
                                    <span className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover/thumb:opacity-100">
                                        <MaximizeIcon className="w-4 h-4 text-white"/>
                                    </span>
                                )}
                            </button>
                            <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate max-w-[160px]">{c.name}</p>
                                {c.delta != null && c.delta !== 0 && (
                                    <p className={`text-xs ${c.delta > 0 ? 'text-green-500' : 'text-red-500'}`}
                                       data-testid="candidate-delta">{c.delta > 0 ? `+${c.delta}` : c.delta} к оценке</p>
                                )}
                                {c.rejectionReason && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"
                                       data-testid="candidate-rejection">
                                        <AlertTriangleIcon className="w-3 h-3 shrink-0"/>
                                        {c.rejectionReason}
                                    </p>
                                )}
                                {c.suitability === 'CAUTION' && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"
                                       data-testid="candidate-suitability">
                                        <AlertTriangleIcon className="w-3 h-3 shrink-0"/>
                                        {c.suitabilityNote || 'Есть замечания по пригодности'}
                                    </p>
                                )}
                            </div>
                            <button type="button" disabled={scoring}
                                    onClick={() => applyReplacement(sr.current.id, sr.slot, c)}
                                    data-testid="replace-btn"
                                    className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-ink-800 disabled:opacity-50 shrink-0">
                                Заменить
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };
    /** R03: «Добавить недостающее» — отдельный блок с собственным действием «Добавить». */
    const renderAdditionGroup = (addition: Addition) => (
        <div key={addition.role} className="space-y-1.5" data-testid="addition-group">
            <p className="text-xs text-gray-400 dark:text-gray-500">
                {addition.roleLabel}: {addition.reason}
            </p>
            {addition.candidates.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="addition-empty">
                    В гардеробе нет подходящей вещи для этой роли.
                </p>
            ) : (
                <div className="flex gap-2 flex-wrap">
                    {addition.candidates.map(c => (
                        <div key={c.itemId} data-testid="addition-candidate"
                             className="flex items-center gap-2.5 rounded-xl border border-gray-200 dark:border-ink-700 pr-2 hover:border-brand-300 transition-colors">
                            <span className="w-12 h-12 rounded-l-xl overflow-hidden bg-gray-100 dark:bg-ink-800 shrink-0 block">
                                {c.primaryPhotoUrl
                                    ? <AuthImage imageKey={c.primaryPhotoUrl} alt={c.name} variant="thumbnail" className="w-full h-full object-cover"/>
                                    : <span className="w-full h-full flex items-center justify-center"><SparklesIcon className="w-5 h-5 text-gray-300"/></span>}
                            </span>
                            <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate max-w-[160px]">{c.name}</p>
                                {c.suitability === 'CAUTION' && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                        <AlertTriangleIcon className="w-3 h-3 shrink-0"/>
                                        {c.suitabilityNote || 'Есть замечания по пригодности'}
                                    </p>
                                )}
                            </div>
                            <button type="button" disabled={scoring}
                                    onClick={() => applyReplacement(null, c.slot ?? null, c)}
                                    data-testid="add-item-btn"
                                    className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-ink-800 disabled:opacity-50 shrink-0">
                                Добавить
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const weatherApplied = r.weatherApplied ?? !!weather?.available;
    const weatherLabel = weatherApplied && weather?.available
        ? `${weather.tempMinC != null ? Math.round(weather.tempMinC) : '?'}° / ${weather.tempMaxC != null ? Math.round(weather.tempMaxC) : '?'}°`
        : 'погода не учтена';
    const weatherFixable = !weatherApplied && !!weather && !weather.available && !!weather.reason
        && FIXABLE_WEATHER_REASONS.has(weather.reason);

    const seedName = r.seedItemName
        ?? currentItems.find(it => it.id === seedItemId)?.name
        ?? 'выбранной вещи';
    // R01: готовность комплекта — источник истины для подписей и действий; покрытие слотов
    // остаётся запасным сигналом для старых ответов без блока readiness.
    const complete = readiness ? isReady(readiness) : (r.complete ?? r.outfit?.complete ?? true);
    const hasOutfit = !!r.outfit && (r.outfit.items?.length ?? 0) > 0;

    const fitsWithState = (r.fits ?? []).map(f => ({fit: f, state: fitState(f)}));
    const okFits = fitsWithState.filter(f => f.state === 'OK');
    const cautionFits = fitsWithState.filter(f => f.state === 'FAIL');
    const unknownFits = fitsWithState.filter(f => f.state === 'UNKNOWN' && f.fit.aspect !== 'weather');
    // Погода не учтена — отдельная нейтральная плашка (QA-2), не «галочка» и не «предупреждение».
    const weatherUnknown = !weatherApplied
        || fitsWithState.some(f => f.fit.aspect === 'weather' && f.state === 'UNKNOWN');
    const outfitReasons = (r.outfit?.reasons ?? []).filter(re => !re.startsWith('Неполный образ'));
    // R11: коды поводов, номера вариантов и уровни автономии уходят в раскрываемые детали.
    const {human: humanReasons, technical: technicalReasons} = splitReasons(outfitReasons);
    const reasons: {text: string; note?: string}[] = modelTextsStale ? [] : [
        ...okFits.map(f => ({text: fitLabel(f.fit, f.state), note: f.fit.note})),
        ...(r.wardrobeNotes ?? []).map(w => ({text: w})),
        ...humanReasons.map(re => ({text: re})),
    ];
    const confLabel = r.confidence ? CONFIDENCE_LABELS[r.confidence] ?? r.confidence : null;

    return (
        <div className="p-4 md:p-6 space-y-6" data-testid="result">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Образ дня</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="flex items-center gap-1.5"><CalendarIcon className="w-4 h-4"/> {today}</span>
                        <span className="flex items-center gap-1.5" data-testid="result-weather-label">
                            {weatherApplied
                                ? <SunIcon className="w-4 h-4 text-amber-400"/>
                                : <CloudIcon className="w-4 h-4 text-gray-400"/>}
                            {weatherLabel}{weatherApplied && weather?.city ? `, ${weather.city}` : ''}
                        </span>
                    </p>
                </div>
                <button onClick={() => setStep(1)}
                        className="px-4 py-2 rounded-xl border border-gray-200 dark:border-ink-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-ink-800">
                    Настроить подбор
                </button>
            </div>

            {msg && <div className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-ink-800 text-sm text-gray-700 dark:text-gray-300">{msg}</div>}

            {/* CTHS-313: образ собран вокруг конкретной вещи — это видно и обратимо. */}
            {seedItemId && (
                <div className="rounded-2xl border border-brand-300 dark:border-brand-500/40 bg-brand-50 dark:bg-brand-900/20 p-3
                                flex flex-wrap items-center justify-between gap-2"
                     data-testid="outfit-seed-banner">
                    <p className="flex items-center gap-2 text-sm text-brand-800 dark:text-brand-200">
                        <SparklesIcon className="w-4 h-4 shrink-0"/>
                        <span>Образ собран вокруг вещи «{seedName}» — она закреплена и остаётся в составе.</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <Link to={`/clothing/${seedItemId}`} data-testid="seed-open-item-link"
                              className="min-h-[44px] sm:min-h-0 px-3 py-2 rounded-xl text-sm text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/40">
                            Открыть вещь
                        </Link>
                        <button onClick={() => navigate('/outfit')} data-testid="seed-clear-btn"
                                className="min-h-[44px] sm:min-h-0 px-3 py-2 rounded-xl text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-ink-800">
                            Снять закрепление
                        </button>
                    </div>
                </div>
            )}

            {!hasOutfit ? (
                <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-8 text-center text-gray-500 dark:text-gray-400"
                     data-testid="result-empty">
                    {r.message ?? 'Недостаточно вещей для образа.'}
                </div>
            ) : (
                <>
                    {/* 1 — Готовность комплекта: полнота важнее балла (R01/R11) */}
                    <OutfitReadinessPanel readiness={readiness} constraints={appliedConstraints}
                                          onComplete={completeInBuilder} updating={scoring}/>

                    {/* 2 — Состав образа: крупные карточки по слотам */}
                    <section className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {complete ? 'Что надеть сегодня' : 'Что уже собрано'}
                                </h2>
                                {r.suggestedName && !modelTextsStale && (
                                    <p className="text-sm text-gray-400 dark:text-gray-500">«{r.suggestedName}»</p>
                                )}
                                {modelTextsStale && (
                                    <p className="text-sm text-gray-400 dark:text-gray-500" data-testid="texts-stale">
                                        Название и советы обновятся при следующем подборе
                                    </p>
                                )}
                            </div>
                            {undoStack.length > 0 && (
                                <button type="button" onClick={undoLastChange} data-testid="undo-change-btn"
                                        className="min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-ink-800">
                                    Вернуть прежнюю вещь
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {items.map(it => (
                                <Link key={it.id} to={`/clothing/${it.id}`} data-testid="outfit-item-link"
                                      title={`Открыть карточку: ${it.name}`}
                                      className="rounded-2xl border border-gray-200 dark:border-ink-700 overflow-hidden bg-white dark:bg-ink-900 block group hover:border-brand-400 hover:shadow-md transition-[border-color,box-shadow] duration-ui ease-out focus:outline-none focus:ring-2 focus:ring-brand-400">
                                    <p className="px-3 pt-2 text-xs text-gray-400 dark:text-gray-500 flex items-center justify-between gap-1">
                                        <span className="truncate">{slotLabel(it.slot)}</span>
                                        {(it.pinned || it.id === seedItemId) && (
                                            <span data-testid="outfit-item-pinned"
                                                  className="shrink-0 px-1.5 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300">
                                                Закреплена
                                            </span>
                                        )}
                                    </p>
                                    <div className="aspect-square bg-gray-100 dark:bg-ink-800 mt-1 relative">
                                        {it.primaryPhotoUrl
                                            ? <AuthImage imageKey={it.primaryPhotoUrl} alt={it.name} variant="medium" className="w-full h-full object-cover"/>
                                            : <div className="w-full h-full flex items-center justify-center"><SparklesIcon className="w-8 h-8 text-gray-300"/></div>}
                                        {it.primaryPhotoUrl && (
                                            <IconButton
                                                label={`Открыть фото «${it.name}» на весь экран`}
                                                title="Открыть фото на весь экран"
                                                size="compact"
                                                onClick={(e) => {e.preventDefault(); e.stopPropagation(); openImageInNewWindow(it.primaryPhotoUrl!);}}
                                                className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-black/55 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-black/75"
                                            >
                                                <MaximizeIcon className="w-4 h-4"/>
                                            </IconButton>
                                        )}
                                    </div>
                                    <p className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{it.name}</p>
                                </Link>
                            ))}
                        </div>
                    </section>

                    {/* Погода не учтена — нейтральная плашка с CTA включить геолокацию (QA-2) */}
                    {weatherUnknown && (
                        weatherFixable ? (
                            <WeatherLocationForm testIdPrefix="outfit-pick"
                                                 title="Погода не учтена: прогноза нет, поэтому соответствие погоде не проверялось. Включите погоду, чтобы подбирать образ под прогноз."
                                                 onSaved={() => pick(exclude)}/>
                        ) : (
                            <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 rounded-xl bg-gray-50 dark:bg-ink-800 p-3"
                               data-testid="outfit-pick-weather-unknown">
                                <CloudIcon className="w-4 h-4 shrink-0"/>
                                Погода не учтена — прогноз недоступен, образ подобран по поводу и стилю.
                            </p>
                        )
                    )}

                    {/* 2 — Почему выбран + AI оценка */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <section className="lg:col-span-2 rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Почему выбран</h3>
                            <ul className="space-y-2.5" data-testid="result-reasons">
                                {reasons.map((re, i) => (
                                    <li key={i} className="flex gap-2 text-sm">
                                        <CheckCheckIcon className="w-4 h-4 text-green-500 shrink-0 mt-0.5"/>
                                        <span>
                                            <span className="text-gray-900 dark:text-gray-100">{re.text}</span>
                                            {re.note && <span className="block text-xs text-gray-400">{re.note}</span>}
                                        </span>
                                    </li>
                                ))}
                                {unknownFits.map(f => (
                                    <li key={f.fit.aspect} className="flex gap-2 text-sm text-gray-400 dark:text-gray-500">
                                        <CloudIcon className="w-4 h-4 shrink-0 mt-0.5 text-gray-300 dark:text-ink-600"/>
                                        <span>{fitLabel(f.fit, f.state)}{f.fit.note ? <span className="block text-xs">{f.fit.note}</span> : null}</span>
                                    </li>
                                ))}
                                {reasons.length === 0 && unknownFits.length === 0 && (
                                    <li className="text-sm text-gray-400">
                                        {modelTextsStale
                                            ? 'Состав изменён — объяснение обновится при следующем подборе.'
                                            : 'AI подобрал лучший доступный образ из гардероба.'}
                                    </li>
                                )}
                            </ul>
                            {technicalReasons.length > 0 && !modelTextsStale && (
                                <details className="text-xs text-gray-400 dark:text-gray-500" data-testid="technical-reasons">
                                    <summary className="cursor-pointer min-h-[44px] sm:min-h-0 flex items-center">
                                        Технические детали подбора
                                    </summary>
                                    <ul className="mt-1 space-y-1 pl-4 list-disc">
                                        {technicalReasons.map((re, i) => <li key={`t${i}`}>{re}</li>)}
                                    </ul>
                                </details>
                            )}
                        </section>

                        <section className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Оценка сочетаемости</h3>
                                {composition && (
                                    <button onClick={resetComposition} data-testid="reset-composition-btn"
                                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline min-h-[44px] sm:min-h-0">
                                        Вернуть исходный состав
                                    </button>
                                )}
                            </div>
                            {shownScore != null ? (
                                <div className="flex items-center gap-3">
                                    <ScoreRing value={Math.round(shownScore)}/>
                                    <div>
                                        <p className={`font-semibold ${complete ? scoreTone(shownScore).text : 'text-amber-600 dark:text-amber-400'}`}
                                           data-testid="result-verdict">
                                            {(composition ? recomposed?.verdict : r.verdict) ?? ''}
                                        </p>
                                        <div className="mt-1 flex flex-wrap items-center gap-2">
                                            <OutfitScoreBadge score={shownScore} label={shownScoreLabel} loading={scoring}/>
                                            {scoreDelta != null && scoreDelta !== 0 && (
                                                <span data-testid="result-score-delta"
                                                      className={`text-xs font-medium ${scoreDelta > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                    {scoreDelta > 0 ? `+${scoreDelta}` : scoreDelta} к оценке после замены
                                                </span>
                                            )}
                                        </div>
                                        {confLabel && !modelTextsStale && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Уверенность: {confLabel}</p>}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {(composition ? recomposed?.verdict : r.verdict) && (
                                        <p className="font-semibold text-gray-700 dark:text-gray-200" data-testid="result-verdict">
                                            {composition ? recomposed?.verdict : r.verdict}
                                        </p>
                                    )}
                                    <OutfitScoreBadge score={null} label={shownScoreLabel} loading={scoring}/>
                                </div>
                            )}
                            {replaceMsg && (
                                <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="result-replace-msg">{replaceMsg}</p>
                            )}
                            {/* F09: сверху причины человеческим языком, техника — под «Деталями расчёта». */}
                            <OutfitScoreDetails diagnostics={composition ? [] : (r.outfit?.topDiagnostics ?? [])}
                                                reasons={composition
                                                    ? recomposed?.scoreReasons
                                                    : (r.scoreReasons ?? undefined)}
                                                cautions={composition
                                                    ? recomposed?.scoreCautions
                                                    : (r.scoreCautions ?? undefined)}
                                                hints={improvementGroups.flatMap(sr => sr.candidates
                                                    .filter(c => (c.delta ?? 0) > 0)
                                                    .map(c => `${sr.slotLabel}: заменить на «${c.name}» (+${c.delta})`))}/>
                            {r.confidence && r.confidence !== 'high' && r.confidenceReason && !modelTextsStale && (
                                <p className="text-xs text-gray-400">{r.confidenceReason}</p>
                            )}
                            {!modelTextsStale && (cautionFits.length > 0 || (r.warnings ?? []).length > 0) && (
                                <ul className="space-y-1.5 pt-1">
                                    {cautionFits.map(f => (
                                        <li key={f.fit.aspect} className="flex gap-2 text-xs">
                                            <AlertTriangleIcon className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5"/>
                                            <span className="text-amber-600 dark:text-amber-400">{f.fit.note || fitLabel(f.fit, f.state)}</span>
                                        </li>
                                    ))}
                                    {(r.warnings ?? []).map((w, i) => (
                                        <li key={`w${i}`} className="flex gap-2 text-xs">
                                            <AlertTriangleIcon className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5"/>
                                            <span className="text-amber-600 dark:text-amber-400">{w}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>

                    {/* 3 — Улучшения: сначала добавить недостающее, потом заменить в образе (R03) */}
                    {(activeAdditions.length > 0 || improvementGroups.length > 0 || alternativeGroups.length > 0) && (
                        <section className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-4"
                                 data-testid="result-replacements">
                            {activeAdditions.length > 0 && (
                                <div className="space-y-3" data-testid="result-additions">
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Добавить недостающее</h3>
                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                            Роли, которые комплект пока не закрывает
                                        </p>
                                    </div>
                                    {activeAdditions.map(addition => renderAdditionGroup(addition))}
                                </div>
                            )}
                            {improvementGroups.length > 0 && (
                                <div className="space-y-3" data-testid="result-improvements">
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Заменить в образе</h3>
                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                            Замены, которые повышают оценку и не ломают комплект
                                        </p>
                                    </div>
                                    {improvementGroups.map(group => renderReplacementGroup(group))}
                                </div>
                            )}
                            {alternativeGroups.length > 0 && (
                                <div className="space-y-3" data-testid="result-alternatives">
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Другие варианты</h3>
                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                            Оценку не повышают либо ломают полноту комплекта — просто другой выбор в этом слоте
                                        </p>
                                    </div>
                                    {alternativeGroups.map(group => renderReplacementGroup(group))}
                                </div>
                            )}
                        </section>
                    )}

                    {/* 4 — Что ещё попробовать (тексты модели — только для исходного состава, R04) */}
                    {!modelTextsStale && (r.tips ?? []).length > 0 && (
                        <section className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-2">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Что ещё можно попробовать</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {(r.tips ?? []).map((t, i) => (
                                    <div key={i} className="rounded-xl border border-gray-200 dark:border-ink-700 p-3">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</p>
                                        {t.note && <p className="text-xs text-gray-400 mt-0.5">{t.note}</p>}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 5 — Действия: одна primary-кнопка на регион — «Надеть» для полного, «Дополнить» для заготовки */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                {complete ? 'Готово к выходу?' : 'Нужно дополнить'}
                            </h3>
                            {!complete && (
                                <button onClick={completeInBuilder} data-testid="complete-outfit-btn-actions"
                                        className="w-full min-h-[44px] px-4 py-2.5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 text-sm font-medium flex items-center justify-center gap-2">
                                    <PlusIcon className="w-4 h-4"/> Добавить в конструкторе
                                </button>
                            )}
                            <button onClick={wearThis} disabled={wearing} data-testid="wear-this-btn"
                                    title={complete ? undefined : 'Комплект неполный — надеть можно, но лучше сначала дополнить'}
                                    className={complete
                                        ? 'w-full min-h-[44px] px-4 py-2.5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60 text-sm font-medium flex items-center justify-center gap-2'
                                        : 'w-full min-h-[44px] px-4 py-2.5 rounded-xl border border-gray-200 dark:border-ink-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-ink-800 disabled:opacity-60 flex items-center justify-center gap-2'}>
                                <CheckCheckIcon className="w-4 h-4"/> {wearing ? 'Сохраняем…' : complete ? 'Надеть этот образ' : 'Всё равно надеть'}
                            </button>
                            <button onClick={saveThis} disabled={savingOnly || wearing} data-testid="save-outfit-btn"
                                    className="w-full min-h-[44px] px-4 py-2.5 rounded-xl border border-gray-200 dark:border-ink-700 text-sm hover:bg-gray-50 dark:hover:bg-ink-800 disabled:opacity-60 flex items-center justify-center gap-2">
                                <BookmarkIcon className="w-4 h-4"/> {savingOnly ? 'Сохраняем…' : 'Сохранить образ'}
                            </button>
                            {complete && (
                                <button onClick={completeInBuilder} data-testid="open-in-builder-btn"
                                        className="w-full min-h-[44px] px-4 py-2.5 rounded-xl border border-gray-200 dark:border-ink-700 text-sm hover:bg-gray-50 dark:hover:bg-ink-800 flex items-center justify-center gap-2">
                                    <HangerIcon className="w-4 h-4"/> Открыть в конструкторе
                                </button>
                            )}
                            <button onClick={anotherPick} disabled={picking} data-testid="another-pick-btn"
                                    className="w-full min-h-[44px] px-4 py-2.5 rounded-xl border border-gray-200 dark:border-ink-700 text-sm hover:bg-gray-50 dark:hover:bg-ink-800 flex items-center justify-center gap-2">
                                <RefreshIcon className="w-4 h-4"/> {seedItemId ? 'Другой образ с этой вещью' : 'Подобрать другой образ'}
                            </button>
                        </div>
                        <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Фото в образе</h3>
                            <p className="text-xs text-gray-400">Загрузи фото, когда наденешь этот образ — AI оценит и подберёт точнее.</p>
                            <input ref={fileInput} type="file" accept="image/*" className="hidden"
                                   onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])}/>
                            <button onClick={() => fileInput.current?.click()}
                                    className="w-full min-h-[44px] px-4 py-2.5 rounded-xl border border-gray-200 dark:border-ink-700 text-sm hover:bg-gray-50 dark:hover:bg-ink-800 flex items-center justify-center gap-2">
                                <CameraIcon className="w-4 h-4"/> Загрузить фото
                            </button>
                            {evalMsg && <p className="text-xs text-gray-500 dark:text-gray-400">{evalMsg}</p>}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default OutfitPage;
